import React, { useState } from 'react';
import { FiHelpCircle } from 'react-icons/fi';
import { useAutoCorrelation } from '../../providers/AutoCorrelation';
import {
  Button,
  Correlation,
  CorrelationGroup,
  CorrelationGroupSelector,
  HelpWindow,
  Tab,
  TabList,
  Tabs,
  XAxisToggle,
  ZoneButton,
  correlationHelpTopics
} from '../../design-system';
import ProjectShell from '../../components/layout/ProjectShell';
import { usePersistentState } from '../../hooks/usePersistentState';
import AutoCorrelationPanel from '../../components/autoCorrelation/AutoCorrelationPanel';
import autoStyles from '../../components/autoCorrelation/AutoCorrelation.module.css';
import { useSelected } from '../../providers/selectedContext';
import SamplesSummary from '../../components/correlation/SamplesSummary';
import { ReactComponent as EmptyIllustration } from '../../assets/empty-chart-correlation.svg';
import styles from './CorrelationPage.module.css';

type IconProps = { size?: number; color?: string; style?: React.CSSProperties };
const HelpIcon = FiHelpCircle as React.FC<IconProps>;

const CorrelationContent = ({
  onHelpOpen,
  activeGroup,
  onActiveGroupChange
}: {
  onHelpOpen: () => void;
  activeGroup: CorrelationGroup;
  onActiveGroupChange: (group: CorrelationGroup) => void;
}) => {
  const { selectedInfo } = useSelected();

  const zones = selectedInfo.project?.correlation?.zones;

  const pageActions = (
    <>
      <ZoneButton />
      <Button variant="secondary" startIcon={<HelpIcon size={14} />} onClick={onHelpOpen}>
        Help
      </Button>
    </>
  );

  if (!zones || zones.length === 0) {
    return (
      <div className={styles.emptyState}>
        <EmptyIllustration style={{ width: '420px' }} />
        <p className={styles.emptyTitle}>No zones created</p>
        <p className={styles.emptySubtitle}>
          Create flow unit zones to run the correlation analysis.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {pageActions}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div style={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        <Correlation
          hideHeader
          showGroupSelector={false}
          activeGroup={activeGroup}
          onActiveGroupChange={onActiveGroupChange}
        />
      </div>
    </div>
  );
};

const CorrelationPage = () => {
  const { selectedInfo } = useSelected();
  const [helpOpen, setHelpOpen] = useState(false);
  // The provider's open flag doubles as the tab selector: the Multi Analysis
  // deep link (?auto=1) lands on the Auto tab, and a successful apply()
  // (which clears the flag) returns the user to the Manual tab.
  const { isOpen, openModal, closeModal } = useAutoCorrelation();
  const activeTab = isOpen ? 'auto' : 'manual';
  // Persist the selected correlation group so it survives reloads/revisits.
  const [activeGroup, setActiveGroup] = usePersistentState<CorrelationGroup>(
    'correlation.activeGroup',
    'capillary'
  );

  const zones = selectedInfo.project?.correlation?.zones;
  const hasZones = Boolean(zones && zones.length > 0);

  return (
    <ProjectShell>
      {!selectedInfo.project ? (
        <div className={styles.emptyState}>
          <EmptyIllustration style={{ width: '420px' }} />
          <p className={styles.emptyTitle}>No project selected</p>
          <p className={styles.emptySubtitle}>
            Select a project from the dashboard to view Capillary Pressure Correlation analysis.
          </p>
        </div>
      ) : (
        <div className={styles.pageRoot}>
          {/* Grey header row hosts the primary Manual/Auto tabs on the left and
              the page actions on the right. The X-axis segmented control and
              the samples summary sit on the white band below (manual only). */}
          <div className={styles.stickyBar}>
            <div className={styles.pageHeaderRow}>
              <Tabs
                value={activeTab}
                onChange={value => (value === 'auto' ? openModal() : closeModal())}
                variant="underline"
                size="md"
              >
                <TabList>
                  <Tab value="manual">Manual Correlation</Tab>
                  <Tab value="auto">
                    Auto Correlation
                    <span className={autoStyles.betaTag}>Beta</span>
                  </Tab>
                </TabList>
              </Tabs>
              <div className={styles.headerActions}>
                <ZoneButton />
                <Button
                  variant="secondary"
                  startIcon={<HelpIcon size={14} />}
                  onClick={() => setHelpOpen(true)}
                >
                  Help
                </Button>
              </div>
            </div>
            {activeTab === 'manual' && (
              <nav className={styles.pageNav}>
                {hasZones && (
                  <CorrelationGroupSelector
                    value={activeGroup}
                    onChange={setActiveGroup}
                  />
                )}
                <XAxisToggle />
                <span className={styles.navDivider} aria-hidden="true" />
                <SamplesSummary inline />
              </nav>
            )}
          </div>
          <div className={styles.tabContent}>
            {activeTab === 'manual' && (
              <CorrelationContent
                onHelpOpen={() => setHelpOpen(true)}
                activeGroup={activeGroup}
                onActiveGroupChange={setActiveGroup}
              />
            )}
            {activeTab === 'auto' && <AutoCorrelationPanel />}
          </div>
        </div>
      )}

      <HelpWindow
        topics={correlationHelpTopics}
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </ProjectShell>
  );
};

export default CorrelationPage;
